import React, { useState, useMemo, useEffect } from "react"
import PropTypes from "prop-types"
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  CartesianGrid, Legend,
} from "recharts"
import {
  formatCurrency, formatNumber, formatPercent,
  familiaConfig, colorPara,
} from "../helpers"

const MAX_SELEC = 4

const ComparadorTab = ({ data }) => {
  const sucursales = useMemo(() => data?.sucursales || [], [data])
  const [seleccion, setSeleccion] = useState([])
  const [busqueda, setBusqueda] = useState("")

  // Auto-precargar las top 3 sedes por impuesto si no hay selección
  useEffect(() => {
    if (seleccion.length === 0 && sucursales.length > 0) {
      const top3 = [...sucursales]
        .sort((a, b) => (b.totales_sucursal?.total_impuestos || 0) - (a.totales_sucursal?.total_impuestos || 0))
        .slice(0, 3)
        .map(s => s.sucursal)
      setSeleccion(top3)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sucursales.length])

  const sucursalesFiltradas = useMemo(() => {
    const q = (busqueda || "").trim().toLowerCase()
    if (!q) return sucursales
    return sucursales.filter(s => s.sucursal.toLowerCase().includes(q))
  }, [sucursales, busqueda])

  const seleccionadas = useMemo(() => {
    return seleccion
      .map(name => sucursales.find(s => s.sucursal === name))
      .filter(Boolean)
  }, [seleccion, sucursales])

  const toggle = (nombre) => {
    setSeleccion(prev => {
      if (prev.includes(nombre)) return prev.filter(s => s !== nombre)
      if (prev.length >= MAX_SELEC) return prev
      return [...prev, nombre]
    })
  }

  // Datos para gráfico comparativo
  const chartData = useMemo(() => {
    return seleccionadas.map((s, i) => {
      const tot = s.totales_sucursal || {}
      const totalExcl = tot.total_excluidos ?? tot.total_exentos ?? s.excluidos?.importe ?? s.exentos?.base_gravable ?? 0
      return {
        sucursal: s.sucursal.length > 25 ? s.sucursal.slice(0, 25) + "…" : s.sucursal,
        sucursal_full: s.sucursal,
        base: Number(tot.total_base_gravable) || 0,
        impuestos: Number(tot.total_impuestos) || 0,
        excluidos: Number(totalExcl) || 0,
        color: colorPara(i),
      }
    })
  }, [seleccionadas])

  // Métricas para "mejor" / "peor"
  const ranking = useMemo(() => {
    const metricas = {}
    seleccionadas.forEach(s => {
      const tot = s.totales_sucursal || {}
      metricas[s.sucursal] = {
        impuestos: tot.total_impuestos || 0,
        base: tot.total_base_gravable || 0,
        gravado: tot.porcentaje_gravado || 0,
        excluido: tot.porcentaje_excluido ?? tot.porcentaje_exento ?? 0,
        lineas: tot.total_lineas || 0,
      }
    })
    const findBest = (key, mayorEsMejor = true) => {
      let best = null
      let bestVal = mayorEsMejor ? -Infinity : Infinity
      Object.entries(metricas).forEach(([nombre, v]) => {
        const val = v[key]
        if (mayorEsMejor ? val > bestVal : val < bestVal) {
          bestVal = val
          best = nombre
        }
      })
      return best
    }
    return {
      mejorImpuestos: findBest("impuestos", true),
      mejorBase: findBest("base", true),
      mejorGravado: findBest("gravado", true),
      menorExcluido: findBest("excluido", false),
      mejorLineas: findBest("lineas", true),
    }
  }, [seleccionadas])

  if (!data || sucursales.length === 0) {
    return (
      <div style={{ padding: 60, textAlign: "center", color: "#64748b" }}>
        <i className="mdi mdi-compare-vertical" style={{ fontSize: 48, color: "#cbd5e1" }} />
        <div className="mt-2">Sin sucursales para comparar.</div>
      </div>
    )
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {/* Selector de sucursales */}
      <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, padding: 14 }}>
        <div className="d-flex justify-content-between align-items-baseline flex-wrap gap-2 mb-2">
          <h6 style={{ margin: 0, color: "#0f172a", fontWeight: 600 }}>
            <i className="mdi mdi-compare-horizontal me-2 text-primary" />
            Comparador de sucursales
          </h6>
          <span style={{ fontSize: 12, color: "#64748b" }}>
            {seleccion.length}/{MAX_SELEC} seleccionadas · click para alternar
          </span>
        </div>
        <input
          type="text"
          placeholder="🔎 Filtrar sucursales…"
          value={busqueda}
          onChange={e => setBusqueda(e.target.value)}
          style={{
            width: "100%", marginBottom: 10, fontSize: 13, padding: "6px 12px",
            borderRadius: 6, border: "1px solid #cbd5e1", height: 34,
          }}
        />
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, maxHeight: 130, overflowY: "auto" }}>
          {sucursalesFiltradas.map(s => {
            const sel = seleccion.includes(s.sucursal)
            const limite = !sel && seleccion.length >= MAX_SELEC
            return (
              <button
                key={s.sucursal}
                type="button"
                onClick={() => toggle(s.sucursal)}
                disabled={limite}
                title={limite ? `Máx ${MAX_SELEC} sucursales` : ""}
                style={{
                  padding: "5px 12px", borderRadius: 999, fontSize: 12,
                  background: sel ? "#3b82f6" : (limite ? "#f1f5f9" : "#fff"),
                  color: sel ? "#fff" : (limite ? "#94a3b8" : "#475569"),
                  border: sel ? "1px solid #3b82f6" : "1px solid #e2e8f0",
                  cursor: limite ? "not-allowed" : "pointer",
                  fontWeight: sel ? 700 : 500,
                  transition: "all 0.15s",
                }}
              >
                {sel && <i className="mdi mdi-check me-1" />}
                {s.sucursal}
              </button>
            )
          })}
        </div>
      </div>

      {seleccionadas.length === 0 && (
        <div style={{ padding: 40, textAlign: "center", color: "#64748b", background: "#fff", border: "1px dashed #cbd5e1", borderRadius: 12 }}>
          <i className="mdi mdi-cursor-pointer" style={{ fontSize: 40, color: "#cbd5e1" }} />
          <div className="mt-2">Seleccioná entre 2 y {MAX_SELEC} sucursales para comparar.</div>
        </div>
      )}

      {seleccionadas.length > 0 && (
        <>
          {/* Cards lado a lado */}
          <div style={{ display: "grid", gridTemplateColumns: `repeat(${Math.min(seleccionadas.length, MAX_SELEC)}, minmax(220px, 1fr))`, gap: 12 }}>
            {seleccionadas.map((s, i) => {
              const t = s.totales_sucursal || {}
              const fams = Array.from(new Set((s.impuestos || []).map(x => x.familia)))
              const color = colorPara(i)
              const totalExcl = t.total_excluidos ?? t.total_exentos ?? s.excluidos?.importe ?? s.exentos?.base_gravable ?? 0
              const pctExcl = t.porcentaje_excluido ?? t.porcentaje_exento ?? 0
              return (
                <div key={s.sucursal} style={{
                  background: "#fff", border: `1px solid #e2e8f0`,
                  borderTop: `4px solid ${color}`, borderRadius: 12, padding: "14px 16px",
                  display: "flex", flexDirection: "column", gap: 8,
                }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 6 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: "#0f172a", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                      title={s.sucursal}>
                      {s.sucursal}
                    </div>
                    <button type="button" onClick={() => toggle(s.sucursal)} title="Quitar"
                      style={{ background: "none", border: "none", color: "#94a3b8", cursor: "pointer", fontSize: 14 }}>
                      <i className="mdi mdi-close" />
                    </button>
                  </div>
                  <div style={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
                    {fams.map(f => {
                      const cfg = familiaConfig(f)
                      return (
                        <span key={f} style={{
                          padding: "1px 7px", borderRadius: 4, fontSize: 9, fontWeight: 700,
                          background: cfg.bg, color: cfg.color,
                        }}>{f}</span>
                      )
                    })}
                  </div>
                  <div style={{ borderTop: "1px solid #f1f5f9", paddingTop: 8, display: "flex", flexDirection: "column", gap: 6 }}>
                    {[
                      { label: "Líneas", val: formatNumber(t.total_lineas), best: ranking.mejorLineas === s.sucursal },
                      { label: "Base", val: formatCurrency(t.total_base_gravable), best: ranking.mejorBase === s.sucursal },
                      { label: "Impuestos", val: formatCurrency(t.total_impuestos), color: "#10b981", best: ranking.mejorImpuestos === s.sucursal },
                      { label: "Excluidos", val: formatCurrency(totalExcl), color: "#f59e0b" },
                      { label: "% Gravado", val: formatPercent(t.porcentaje_gravado, 2), best: ranking.mejorGravado === s.sucursal },
                      { label: "% Excluido", val: formatPercent(pctExcl, 2), best: ranking.menorExcluido === s.sucursal },
                    ].map((m) => (
                      <div key={m.label} style={{
                        display: "flex", justifyContent: "space-between",
                        fontSize: 12,
                      }}>
                        <span style={{ color: "#64748b" }}>{m.label}</span>
                        <span style={{
                          fontWeight: 600,
                          color: m.color || "#0f172a",
                          display: "inline-flex", alignItems: "center", gap: 4,
                        }}>
                          {m.val}
                          {m.best && <i className="mdi mdi-medal-outline" style={{ color: "#f59e0b", fontSize: 14 }} title="Mejor del comparativo" />}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )
            })}
          </div>

          {/* Bar chart comparativo */}
          {seleccionadas.length >= 2 && (
            <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 14, padding: 16 }}>
              <h6 style={{ margin: 0, color: "#0f172a", fontWeight: 600, marginBottom: 8 }}>
                <i className="mdi mdi-chart-bar me-2 text-primary" />
                Comparativo visual
              </h6>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={chartData} margin={{ left: 0, right: 12 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="sucursal" tick={{ fontSize: 11 }} interval={0} angle={-15} height={50} textAnchor="end" />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={v => formatCurrency(v).replace("COP", "").trim()} />
                  <Tooltip
                    formatter={v => formatCurrency(v)}
                    labelFormatter={(_, payload) => payload?.[0]?.payload?.sucursal_full || ""}
                  />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="base"      name="Base gravable" fill="#94a3b8" radius={[6, 6, 0, 0]} />
                  <Bar dataKey="impuestos" name="Impuestos"     fill="#3b82f6" radius={[6, 6, 0, 0]} />
                  <Bar dataKey="excluidos" name="Excluidos"     fill="#f59e0b" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </>
      )}
    </div>
  )
}

ComparadorTab.propTypes = { data: PropTypes.object }

export default ComparadorTab
